import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Text, Heading, ProgressCircle, Divider, StatusLight } from "@react-spectrum/s2";
import { CCOrganization, CCSite } from '../../types';
import { DomainSortKey, OverviewDomainRow, SortDirection } from '../../types/overview';
import { fetchCCOrganizationSummary, fetchCCOrgAnalyticsDomains } from '../../services/api';
import { UsageProgressBar } from '../common/UsageProgressBar';
import { formatDate } from '../../utils/dateUtils';
import { formatCount } from '../../utils/numberUtils';
import { DomainsOverviewTable, appendUniqueDomainRows } from '../Overview/DomainsOverviewTable';
import { DomainDetailPanel } from './DomainDetailPanel';
import { ModelAllowlistPanel } from './ModelAllowlistPanel';
import { OrgConfigurationPanel } from './OrgConfigurationPanel';
import { OrgAdminsPanel } from './OrgAdminsPanel';
import { calcPercentage, formatUsageLabel } from './utils';
import { DomainPageShell } from '../common/DomainPageShell';
import { useSiteContext } from '../../contexts/SiteContext';
interface OrganizationDetailViewProps {
    organization: CCOrganization;
}
const DOMAINS_PAGE_SIZE = 10;
interface DomainsData {
    rows: OverviewDomainRow[];
    total: number;
    hasMore: boolean;
}
// Matches design-frames/org-detail.html's TITLES map — the main content
// header shows the section name, not the org name (which lives in the
// primary sidebar's header instead, via OrgDetailSidebar).
const SECTION_TITLES = (t: TFunction): Record<'domains' | 'orgconfig' | 'orgadmins' | 'allowlist', string> =>({
        domains: t('commandCenter.orgDetailView.sectionTitles.domains'),
        orgconfig: t('commandCenter.orgDetailView.sectionTitles.orgconfig'),
        orgadmins: t('commandCenter.orgDetailView.sectionTitles.orgadmins'),
        allowlist: t('commandCenter.orgDetailView.sectionTitles.allowlist')
    });
export const OrganizationDetailView: React.FC<OrganizationDetailViewProps> = ({ organization: initialOrg })=>{
    const { t } = useTranslation();
    const { orgDetailSection, selectedDomainSiteId } = useSiteContext();
    const [orgData, setOrgData] = useState<CCOrganization | null>(null);
    // Falls back to the loaded DomainDetailPanel's own fetch when the deep-linked
    // domain isn't present in whatever page of domainsData.rows is loaded.
    const [selectedDomainSite, setSelectedDomainSite] = useState<CCSite | null>(null);
    const [domainsData, setDomainsData] = useState<DomainsData>({
        rows: [],
        total: 0,
        hasMore: false
    });
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [sortBy, setSortBy] = useState<DomainSortKey>('site_name');
    const [sortDir, setSortDir] = useState<SortDirection>('asc');
    const currentPageRef = useRef(1);
    // S2's load-more sentinel can fire onLoadMore several times before the
    // `loadingMore` state commits; a ref blocks those synchronously. Duplicate
    // appended rows produce duplicate collection keys, which hang React Aria.
    const loadMoreInFlightRef = useRef(false);
    const loadOrgSummary = useCallback(async ()=>{
        try {
            const summary = await fetchCCOrganizationSummary(initialOrg.org_id);
            setOrgData(summary);
        } catch (err) {
            setError(err instanceof Error ? err : new Error(t('commandCenter.orgDetailView.failedToLoadSummary')));
        }
    }, [
        initialOrg.org_id,
        t
    ]);
    const loadDomainsOverview = useCallback(async (page: number, isLoadMore = false)=>{
        if (isLoadMore) {
            if (loadMoreInFlightRef.current) return;
            loadMoreInFlightRef.current = true;
        }
        try {
            if (isLoadMore) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }
            const response = await fetchCCOrgAnalyticsDomains(initialOrg.org_id, {
                page,
                pageSize: DOMAINS_PAGE_SIZE,
                sortBy,
                sortDir
            });
            const hasMore = response.rows.length >= DOMAINS_PAGE_SIZE && page * DOMAINS_PAGE_SIZE < response.total_count;
            if (isLoadMore) {
                setDomainsData((prev)=>({
                        rows: appendUniqueDomainRows(prev.rows, response.rows),
                        total: response.total_count,
                        hasMore
                    }));
            } else {
                setDomainsData({
                    rows: response.rows,
                    total: response.total_count,
                    hasMore
                });
            }
            currentPageRef.current = page;
            setError(null);
        } catch (err) {
            setError(err instanceof Error ? err : new Error(t('commandCenter.orgDetailView.failedToLoadDomains')));
        } finally{
            if (isLoadMore) loadMoreInFlightRef.current = false;
            setLoading(false);
            setLoadingMore(false);
        }
    }, [
        initialOrg.org_id,
        sortBy,
        sortDir,
        t
    ]);
    // Initial load — org summary only; domains load is handled by the
    // sort-driven effect below, which also fires once on mount with the
    // default sort (avoids double-fetching page 1 on load).
    useEffect(()=>{
        loadOrgSummary();
    }, [
        loadOrgSummary
    ]);
    const handleSortChange = useCallback((nextSortBy: DomainSortKey, nextSortDir: SortDirection)=>{
        setSortBy(nextSortBy);
        setSortDir(nextSortDir);
        currentPageRef.current = 1;
    }, []);
    // Loads domains for the current org whenever the org, or the sort,
    // changes — and once on mount, since sortBy/sortDir start at defined
    // defaults. Depends on initialOrg.org_id directly (not just
    // loadDomainsOverview) so a parent re-render with a different org — no
    // remount guaranteed — still triggers a reload.
    useEffect(()=>{
        loadDomainsOverview(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        initialOrg.org_id,
        sortBy,
        sortDir
    ]);
    // Clears the DomainDetailPanel-sourced fallback name when navigating to a
    // different (or no) domain, so a stale previous title never flashes while
    // the new domain's own fetch is in flight.
    useEffect(()=>{
        setSelectedDomainSite(null);
    }, [
        selectedDomainSiteId
    ]);
    const handleLoadMore = useCallback(()=>{
        if (!loadingMore && domainsData.hasMore) {
            loadDomainsOverview(currentPageRef.current + 1, true);
        }
    }, [
        loadingMore,
        domainsData.hasMore,
        loadDomainsOverview
    ]);
    const handleOrgConfigSaved = useCallback(async ()=>{
        await loadOrgSummary();
        await loadDomainsOverview(1);
    }, [
        loadOrgSummary,
        loadDomainsOverview
    ]);
    const loadingState = loading ? 'loading' : loadingMore ? 'loadingMore' : 'idle';
    const orgType = orgData?.type === 'SANDBOX' ? 'sandbox' : 'provisioned';
    const selectedDomainName = selectedDomainSiteId ? domainsData.rows.find((r)=>r.site_id === selectedDomainSiteId)?.site_name ?? (selectedDomainSite?.site_id === selectedDomainSiteId ? selectedDomainSite.site_name : null) : null;
    const sectionTitles = useMemo(()=>SECTION_TITLES(t), [
        t
    ]);
    const title = selectedDomainName ?? sectionTitles[orgDetailSection];
    const renderDomainsSection = (): React.ReactNode =>{
        if (selectedDomainSiteId) {
            return (<div className="org-resource-page-body">
          <DomainDetailPanel siteId={selectedDomainSiteId} orgId={initialOrg.org_id} onSiteLoaded={setSelectedDomainSite}/>
        </div>);
        }
        if (loading && !orgData) {
            return (<div className="cc-org-detail-content">
          <div className={"cc-loading-container" + " sd171 _ta171 eb171 _Ca171 MieMuJb171"}>
            <ProgressCircle aria-label={t('commandCenter.orgDetailView.loading')} isIndeterminate/>
          </div>
        </div>);
        }
        if (error) {
            return (<div className="cc-org-detail-content">
          <div className={"cc-error-container" + " sd171 _ta171 eb171 _Ca171 MieMuJb171"}>
            <Text UNSAFE_className="cc-error-text">{error.message}</Text>
          </div>
        </div>);
        }
        if (!orgData) return null;
        return (<div className="cc-org-detail-content">
        {}
        <div className="cc-org-card">
          <div className={" sd171 _Ce171 eb171 GA171"}>
            <StatusLight variant={orgType === 'sandbox' ? "informative" : 'positive'}>
              {orgType === 'sandbox' ? t('commandCenter.orgDetailView.sandbox') : t('commandCenter.orgDetailView.provisioned')}
            </StatusLight>
          </div>

          {}
          <div className={"cc-info-grid cc-info-grid--4col" + " sd171"}>
            <div className="cc-info-item">
              <Text UNSAFE_className="cc-info-label">{t('commandCenter.orgDetailView.organizationId')}</Text>
              <Text UNSAFE_className="cc-info-value">{orgData.org_id}</Text>
            </div>
            <div className="cc-info-item">
              <Text UNSAFE_className="cc-info-label">{t('commandCenter.orgDetailView.organizationName')}</Text>
              <Text UNSAFE_className="cc-info-value">{orgData.org_name}</Text>
            </div>
            <div className="cc-info-item">
              <Text UNSAFE_className="cc-info-label">{t('commandCenter.orgDetailView.createdBy')}</Text>
              <Text UNSAFE_className="cc-info-value">{orgData.created_by}</Text>
            </div>
            <div className="cc-info-item">
              <Text UNSAFE_className="cc-info-label">{t('commandCenter.orgDetailView.subscriptionEnd')}</Text>
              <Text UNSAFE_className="cc-info-value">{formatDate(orgData.subscription_end)}</Text>
            </div>
          </div>

          {}
          <Divider size="S" styles={" JC171 GC171"}/>
          <Heading level={4} UNSAFE_className="cc-resource-title">{t('commandCenter.orgDetailView.organizationBudget')}</Heading>

          <div className={"cc-resource-row" + " sd171 Uj171 qj171"}>
            {}
            <div className={"cc-resource-item" + " _ub171 _vb171 _sb171"}>
              <div className={" sd171 _Ce171 eb171 Gs171"}>
                <Text UNSAFE_className="cc-resource-label">{t('commandCenter.orgDetailView.domains')}</Text>
                <Text UNSAFE_className="cc-resource-limit">{formatCount(orgData.domain?.total ?? 0)}</Text>
              </div>
              <UsageProgressBar used={orgData.domain?.used ?? 0} total={orgData.domain?.total ?? 0} size="lg"/>
              <div className={" sd171 _Ce171 eb171 Js171"}>
                <Text UNSAFE_className="cc-resource-used">
                  {t('commandCenter.orgDetailView.created', {
            usage: formatUsageLabel(orgData.domain?.used ?? 0, orgData.domain?.total ?? 0)
        })}
                </Text>
                <Text UNSAFE_className="cc-resource-percent">
                  {t('commandCenter.orgDetailView.utilized', {
            percent: calcPercentage(orgData.domain?.used ?? 0, orgData.domain?.total ?? 0)
        })}
                </Text>
              </div>
            </div>

            {}
            <div className={"cc-resource-item" + " _ub171 _vb171 _sb171"}>
              <div className={" sd171 _Ce171 eb171 Gs171"}>
                <Text UNSAFE_className="cc-resource-label">{t('commandCenter.orgDetailView.files')}</Text>
                <Text UNSAFE_className="cc-resource-limit">{formatCount(orgData.document_indexed?.total ?? 0)}</Text>
              </div>
              <UsageProgressBar used={orgData.document_indexed?.used ?? 0} total={orgData.document_indexed?.total ?? 0} size="lg"/>
              <div className={" sd171 _Ce171 eb171 Js171"}>
                <Text UNSAFE_className="cc-resource-used">
                  {t('commandCenter.orgDetailView.indexed', {
            usage: formatUsageLabel(orgData.document_indexed?.used ?? 0, orgData.document_indexed?.total ?? 0)
        })}
                </Text>
                <Text UNSAFE_className="cc-resource-percent">
                  {t('commandCenter.orgDetailView.utilized', {
            percent: calcPercentage(orgData.document_indexed?.used ?? 0, orgData.document_indexed?.total ?? 0)
        })}
                </Text>
              </div>
            </div>

            {}
            <div className={"cc-resource-item" + " _ub171 _vb171 _sb171"}>
              <div className={" sd171 _Ce171 eb171 Gs171"}>
                <Text UNSAFE_className="cc-resource-label">{t('commandCenter.orgDetailView.queries')}</Text>
                <Text UNSAFE_className="cc-resource-limit">{formatCount(orgData.request_usage?.total ?? 0)}</Text>
              </div>
              <UsageProgressBar used={orgData.request_usage?.used ?? 0} total={orgData.request_usage?.total ?? 0} size="lg"/>
              <div className={" sd171 _Ce171 eb171 Js171"}>
                <Text UNSAFE_className="cc-resource-used">
                  {t('commandCenter.orgDetailView.requests', {
            usage: formatUsageLabel(orgData.request_usage?.used ?? 0, orgData.request_usage?.total ?? 0)
        })}
                </Text>
                <Text UNSAFE_className="cc-resource-percent">
                  {t('commandCenter.orgDetailView.utilized', {
            percent: calcPercentage(orgData.request_usage?.used ?? 0, orgData.request_usage?.total ?? 0)
        })}
                </Text>
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="cc-managed-sites-section">
          <DomainsOverviewTable rows={domainsData.rows} tableLabel={t('commandCenter.orgDetailView.domainTableLabel')} loading={loading} loadingState={loadingState} sortBy={sortBy} sortDir={sortDir} onSortChange={handleSortChange} onLoadMore={handleLoadMore}/>
        </div>
      </div>);
    };
    return (<DomainPageShell title={title}>
      {orgDetailSection === 'domains' && renderDomainsSection()}

      {orgDetailSection === 'orgconfig' && orgData && (<div className="org-resource-page-body--wide">
          <OrgConfigurationPanel organization={orgData} onSaved={handleOrgConfigSaved}/>
        </div>)}

      {orgDetailSection === 'orgadmins' && (<div className={"org-resource-page-body--wide" + " sd171 _ta171 Ma171"} style={{
        height: '100%'
    }}>
          <OrgAdminsPanel orgId={initialOrg.org_id}/>
        </div>)}

      {orgDetailSection === 'allowlist' && (<div className={"org-resource-page-body" + " sd171 _ta171 Ma171"} style={{
        height: '100%'
    }}>
          <ModelAllowlistPanel orgId={initialOrg.org_id}/>
        </div>)}
    </DomainPageShell>);
};

import "macro-99e36016246c8dc5f1a6e7e1c0c289d25644e8133f51fc06155133a450a9d334.css";
import "macro-99e36016246c8dc5f1a6e7e1c0c289d25644e8133f51fc06155133a450a9d334.css";
import "macro-870dd6a49ebbc94fdd136d735a12cc435675dc4285f77ea6902a21b09f6174ac.css";
import "macro-77707702effd8810108fcdc5b720e8ad8baeef24b4d1d8c05e38347fdbc53e75.css";
import "macro-f6787f6c8dd8764009f437e28e2c4ffd49461092fc81eff8be91a446e3e0d908.css";
import "macro-1d0e244b9edb47690882da6d22f2153aaa258e58adca0a912e152be84bdc2d2d.css";
import "macro-bab5ef82f09fb5d0f4436f805cbbf1506e94fd514257b2e4f43932c802b98f56.css";
import "macro-707b4b222b23a8e8cc49cb669b218a266687d688ff939c4fb53873e1c72c84c6.css";
import "macro-74a731a03699865fe7adeae9b78250e430f08ad0e9a2a3bc7aa561e6053ab690.css";
import "macro-bab5ef82f09fb5d0f4436f805cbbf1506e94fd514257b2e4f43932c802b98f56.css";
import "macro-707b4b222b23a8e8cc49cb669b218a266687d688ff939c4fb53873e1c72c84c6.css";
import "macro-74a731a03699865fe7adeae9b78250e430f08ad0e9a2a3bc7aa561e6053ab690.css";
import "macro-bab5ef82f09fb5d0f4436f805cbbf1506e94fd514257b2e4f43932c802b98f56.css";
import "macro-707b4b222b23a8e8cc49cb669b218a266687d688ff939c4fb53873e1c72c84c6.css";
import "macro-74a731a03699865fe7adeae9b78250e430f08ad0e9a2a3bc7aa561e6053ab690.css";
import "macro-fa74f23d5104be2bbd303f1b8d985ee48e18dfaf362051f4b04d3443a193b9e8.css";
import "macro-fa74f23d5104be2bbd303f1b8d985ee48e18dfaf362051f4b04d3443a193b9e8.css";